import { SITE } from "../../constants";

/**
 * Helper to turn http/https URLs in text into clickable links
 */
function renderLinksInText(text) {
  if (!text) return null;
  const parts = text.split(/(https?:\/\/[^\s)]+)/g);

  return parts.map((part, index) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-purple-700 underline underline-offset-2 hover:text-purple-900 transition-colors"
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

/**
 * MessageContent component renders chat message body.
 * - Handles user bubble text cleanly.
 * - For assistant messages, cleanly extracts and highlights Direct Support
 *   details (WhatsApp & Email) as interactive, clickable contact actions.
 */
export default function MessageContent({ text, isUser = false }) {
  if (!text) return null;

  if (isUser) {
    return <span className="whitespace-pre-line break-words">{text}</span>;
  }

  // Check if text has Direct Support / contact info
  const supportMatch = text.match(/(Direct Support:?|WhatsApp:\s*\+91|Email:\s*hello@)/i);
  let mainText = text;
  let showDirectSupport = false;

  if (supportMatch) {
    const splitIndex = supportMatch.index;
    mainText = text.substring(0, splitIndex).trim();
    showDirectSupport = true;
  }

  return (
    <div className="space-y-3">
      {/* Main message body */}
      {mainText && (
        <div className="whitespace-pre-line break-words leading-relaxed text-[#1E1E1E]">
          {renderLinksInText(mainText)}
        </div>
      )}

      {/* Structured Direct Support actions */}
      {showDirectSupport && (
        <div className="mt-3 border-t border-purple-100/90 pt-3" data-testid="askpurple-direct-support">
          <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-purple-900/60">
            Direct Support
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={SITE.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/80 px-3 py-1.5 text-xs font-semibold text-emerald-800 shadow-sm transition hover:bg-emerald-100 hover:border-emerald-300"
              data-testid="askpurple-whatsapp-link"
              title="Chat on WhatsApp"
            >
              <span className="text-sm">💬</span>
              <span>WhatsApp: {SITE.whatsappDisplay}</span>
            </a>

            <a
              href={`mailto:${SITE.email}`}
              className="inline-flex items-center gap-2 rounded-xl border border-purple-200 bg-purple-50/80 px-3 py-1.5 text-xs font-semibold text-purple-800 shadow-sm transition hover:bg-purple-100 hover:border-purple-300"
              data-testid="askpurple-email-link"
              title="Send an Email"
            >
              <span className="text-sm">✉️</span>
              <span>Email: {SITE.email}</span>
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
